import React from 'react';
import { LogIn, ArrowRight } from 'lucide-react';
import { useTeamContext } from '../../contexts/TeamContext';

interface PublicTopNavigationProps {
  currentUser?: {
    id: string;
    name: string;
    email: string;
  };
}

export function PublicTopNavigation({ currentUser }: PublicTopNavigationProps) {
  const { teamName, teamSubdomain, teamLogoUrl, teamStyle } = useTeamContext();

  // Debug logging
  console.log('[PublicTopNavigation] currentUser prop:', currentUser);
  console.log('[PublicTopNavigation] teamStyle:', teamStyle);

  // Use team's primary color (header_color) for buttons, fallback to blue
  const primaryColor = teamStyle?.headerColor || '#2563eb'; // Default to blue-600

  // Helper function to darken a hex color for hover state
  const darkenHexColor = (hex: string, percent: number = 10): string => {
    // Remove # if present
    const cleanHex = hex.replace('#', '');

    // Convert to RGB
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);

    // Darken
    const darkenValue = (value: number) => Math.max(0, Math.floor(value * (1 - percent / 100)));

    // Convert back to hex
    const toHex = (value: number) => value.toString(16).padStart(2, '0');

    return `#${toHex(darkenValue(r))}${toHex(darkenValue(g))}${toHex(darkenValue(b))}`;
  };

  const hoverColor = darkenHexColor(primaryColor, 15);

  return (
    <nav className="bg-white border-b border-gray-200 fixed top-0 left-0 right-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo/Team Name */}
          <div className="flex-shrink-0">
            <a href="/" className="flex items-center hover:opacity-80 transition-opacity">
              {teamLogoUrl ? (
                <div className="py-2">
                  <img
                    src={teamLogoUrl}
                    alt={teamName || 'Team'}
                    className="h-8 w-auto object-contain"
                    style={{ maxWidth: '200px' }}
                  />
                </div>
              ) : (
                <div className="flex items-center">
                  <div
                    className="text-white font-semibold rounded-lg p-2"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <span className="hidden sm:block">
                      {teamName ? teamName.split(' ').map(word => word[0]).join('').substring(0, 2).toUpperCase() : 'TM'}
                    </span>
                    <span className="block sm:hidden">
                      {teamName ? teamName[0].toUpperCase() : 'T'}
                    </span>
                  </div>
                  <span className="ml-2 text-gray-900 font-medium hidden sm:block">
                    {teamName || 'Team'}
                  </span>
                </div>
              )}
            </a>
          </div>

          {/* User Menu */}
          <div className="flex items-center">
            {currentUser ? (
              <a
                href="/accounts"
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-all duration-200"
                style={{
                  backgroundColor: primaryColor,
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = hoverColor;
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = primaryColor;
                  e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                }}
              >
                <span>Enter Community</span>
                <ArrowRight className="h-4 w-4" />
              </a>
            ) : (
              <a
                href="/accounts/sign_in"
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-all duration-200"
                style={{
                  backgroundColor: primaryColor,
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = hoverColor;
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = primaryColor;
                  e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                }}
              >
                <LogIn className="h-4 w-4" />
                <span>Sign In</span>
              </a>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}